// Drama for a really tight race: a duel for the line, and the finish video after a photo finish.
// Kept restrained on purpose: one replay per race, a short slow-motion ramp at the line, and
// no replay while anything else is happening (a catnip trip, a cat about to finish, a lost signal,
// or a viewer who picked their own camera).
import { TRACK_LENGTH } from '../data/track';
import type { CatId, CatSnapshot, RaceEvent, RaceSnapshot } from '../race/types';

/** Two cats count as duelling when the race is this far along… */
export const DUEL_FROM = 0.85;
/** …until this close to the line, where the finish camera takes over… */
export const DUEL_TO = 0.955;
/** …and they are at most this many metres apart. */
export const DUEL_GAP_M = 6;

export interface Duel { leader: CatSnapshot; rival: CatSnapshot; gapM: number }

/** The two front cats still running, when they are fighting for the line. Pure. */
export function duel(s: RaceSnapshot): Duel | null {
  if (s.phase !== 'running') return null;
  const live = s.cats.filter((c) => !c.finished && !c.tripping).sort((a, b) => b.displayPos - a.displayPos);
  const [leader, rival] = live;
  if (!leader || !rival) return null;
  const gapM = (leader.displayPos - rival.displayPos) * TRACK_LENGTH;
  if (leader.displayPos < DUEL_FROM || leader.displayPos >= DUEL_TO || gapM > DUEL_GAP_M) return null;
  return { leader, rival, gapM };
}

export const REPLAY = {
  /** Let the live crossing and the place call breathe before the replay starts. */
  delayMs: 1200,
  /** If the line is not clear for a replay by this long after the crossing, skip it. */
  waitMs: 8000,
  /** The replay shows this much race time before the first crossing… */
  beforeMs: 2000,
  /** …and this much after the last one. */
  afterMs: 700,
  /** Slowest replay speed, right at the line. */
  slowest: 0.3,
  /** The speed ramps from normal to slowest over this much race time either side of the line. */
  rampMs: 900,
  /** A running cat this close to the line would finish during the replay: wait for it. */
  clearPos: 0.93,
} as const;

/** What the world shows during the replay. */
export interface ReplayFrame {
  /** The race as it was, at replay time. */
  snapshot: RaceSnapshot;
  /** Replay speed now: 1 is normal, less is slow motion. Scale animation time by it. */
  rate: number;
  /** The cats in the photo finish: the one that crossed first, then the one just behind. */
  cats: [CatId, CatId];
  /** 0 at the start of the replay, 1 at its end. */
  progress: number;
}

interface Window { raceNo: number; from: number; to: number; cats: [CatId, CatId] }

const smooth = (a: number, b: number, x: number) => { const u = Math.min(1, Math.max(0, (x - a) / (b - a))); return u * u * (3 - 2 * u); };

/**
 * Keeps the last few seconds of the race and, after a close finish, plays them back slowly.
 * Fed the live snapshot every frame; returns the replay frame while the replay runs.
 */
export class FinishReplay {
  private history: RaceSnapshot[] = [];
  private pending: Window | null = null;
  private playing: (Window & { tau: number }) | null = null;
  private replayedRace = -1;

  /** step: render time since the last frame, s. allowed: the director is choosing the shots. */
  update(s: RaceSnapshot, events: RaceEvent[], step: number, allowed: boolean): ReplayFrame | null {
    const last = this.history[this.history.length - 1];
    if (last && s.t < last.t) { this.history = []; this.pending = null; this.playing = null; }
    if (!last || s.t > last.t) this.history.push(s);
    this.notice(s, events);
    this.trim(s);

    if (this.playing) {
      const p = this.playing;
      const busy = s.cats.some((c) => c.tripping) || s.signal === 'lost' || s.raceNo !== p.raceNo;
      if (!allowed || busy) { this.playing = null; return null; }
      p.tau += step * 1000 * this.rate(p, p.tau);
      if (p.tau >= p.to + REPLAY.afterMs) { this.playing = null; return null; }
      return this.frame(p);
    }

    const w = this.pending;
    if (!w) return null;
    if (w.raceNo !== s.raceNo || s.t > w.to + REPLAY.delayMs + REPLAY.waitMs) { this.pending = null; return null; }
    if (s.t < w.to + REPLAY.delayMs || !allowed || !this.clear(s)) return null;
    const start = Math.max(w.from - REPLAY.beforeMs, this.history[0]?.t ?? w.from);
    this.pending = null;
    this.replayedRace = w.raceNo;
    this.playing = { ...w, tau: start };
    return this.frame(this.playing);
  }

  /** A close finish: plan its replay, once per race. Crossings just after it join the same replay. */
  private notice(s: RaceSnapshot, events: RaceEvent[]): void {
    for (const e of events) {
      if (e.type !== 'close-finish') continue;
      // At the results frame, every cat still out on the course "finishes" at once: not a photo finish.
      const atResults = s.phase === 'finished' || events.some((x) => x.type === 'phase' && x.phase === 'finished' && x.t <= e.t);
      if (atResults || this.replayedRace === s.raceNo || this.playing) continue;
      // The event comes when the second cat crosses; the first crossed marginMs earlier.
      if (this.pending && this.pending.raceNo === s.raceNo) this.pending.to = Math.max(this.pending.to, e.t);
      else this.pending = { raceNo: s.raceNo, from: e.t - e.marginMs, to: e.t, cats: e.cats };
    }
  }

  /** Keep enough of the past for the replay that might come. */
  private trim(s: RaceSnapshot): void {
    const w = this.playing ?? this.pending;
    const keepFrom = w ? w.from - REPLAY.beforeMs - 200 : s.t - REPLAY.beforeMs - REPLAY.delayMs;
    let drop = 0;
    while (drop < this.history.length - 2 && this.history[drop + 1]!.t <= keepFrom) drop++;
    if (drop) this.history.splice(0, drop);
  }

  /** Nothing live worth missing for a few seconds. */
  private clear(s: RaceSnapshot): boolean {
    if (s.signal === 'lost' || s.phase === 'starting') return false;
    return !s.cats.some((c) => c.tripping || (!c.finished && s.phase === 'running' && c.displayPos > REPLAY.clearPos));
  }

  private rate(w: Window, tau: number): number {
    const d = tau < w.from ? w.from - tau : tau > w.to ? tau - w.to : 0;
    return REPLAY.slowest + (1 - REPLAY.slowest) * smooth(0, REPLAY.rampMs, d);
  }

  private frame(p: Window & { tau: number }): ReplayFrame {
    const start = p.from - REPLAY.beforeMs;
    const progress = Math.min(1, Math.max(0, (p.tau - start) / (p.to + REPLAY.afterMs - start)));
    return { snapshot: this.sample(p.tau), rate: this.rate(p, p.tau), cats: p.cats, progress };
  }

  /** The race at time tau, eased between the two recorded snapshots around it. */
  private sample(tau: number): RaceSnapshot {
    const h = this.history;
    let i = 0;
    while (i < h.length - 2 && h[i + 1]!.t <= tau) i++;
    const a = h[i]!, b = h[i + 1] ?? a;
    const k = b.t > a.t ? Math.min(1, Math.max(0, (tau - a.t) / (b.t - a.t))) : 0;
    const cats = a.cats.map((c) => {
      const n = b.cats.find((x) => x.id === c.id) ?? c;
      return { ...c, displayPos: c.displayPos + (n.displayPos - c.displayPos) * k, speed: c.speed + (n.speed - c.speed) * k };
    });
    return { ...a, t: tau, cats };
  }
}
